import {
  Flex,
  Text,
  Paper,
  Table,
  ActionIcon,
  Box,
  Collapse,
  Badge,
  Chip,
  Tabs,
  ScrollArea,
} from '@mantine/core';
import {
  IconTarget,
  IconChartBar,
  IconReportAnalytics,
  IconSettings,
} from '@tabler/icons-react';
import { ALL_METRICS, GoalRow, LifecycleData } from './types';
import {
  SectionHeader,
  GoalBreakdownTypeSelect,
  GoalBreakdownTable,
  MetricSource,
  MetricCellTd,
  SectionHelp,
} from './shared';
import { AggregatedLifecycleBody } from './AggregatedLifecycleBody';
import { AdvancedFilterTables, AdvancedFilterTablesData } from './AdvancedFilterTables';
import { ContributionChart, ContributionData } from './ContributionChart';
import { SourcesChart, SourcesData } from './SourcesChart';
import { CostMetric, InsightsInfo, TrendChart, TrendData, VelocityChart, VelocityData } from './InsightsCharts';

type TotalTabProps = {
  goalRows: GoalRow[];
  // Passed straight through to GoalBreakdownTable; this tab owns no Customer Type state.
  goalCustomerType?: any;
  primaryGoalSummary?: MetricSource | null;
  lifecycleData: LifecycleData | null;
  lifecycleLoading?: boolean;
  isDarkTheme?: boolean;
  // Subtitles for the charts whose text is built by the backend (Contribution, Sources).
  insightsSubtitles: Record<string, string>;
  insightsChart: string;
  setInsightsChart: (v: string) => void;
  trendData?: TrendData | null;
  velocityData?: VelocityData | null;
  insightsCompare: Record<string, boolean>;
  onInsightsCompareChange: (chart: string, v: boolean) => void;
  cpcTrendMetric: CostMetric;
  onCpcTrendMetricChange: (v: CostMetric) => void;
  onVelocityFromChange: (v: string) => void;
  contributionData?: ContributionData | null;
  sourcesData?: SourcesData | null;
  openSettings: (context: string, scope: string) => void;
  totalMetrics: string[];
  primaryGoalNames?: string[];
  selectedPrimaryGoalName?: string;
  advancedFilterTables?: AdvancedFilterTablesData;
  goalBreakdownOpened: boolean;
  goalBreakdownType: 'none' | 'device' | 'hour' | 'source';
  onGoalBreakdownTypeChange: (v: 'none' | 'device' | 'hour' | 'source') => void;
  setGoalBreakdownOpened: (v: boolean | ((prev: boolean) => boolean)) => void;
  lifecycleOpened: boolean;
  setLifecycleOpened: (v: boolean | ((prev: boolean) => boolean)) => void;
  insightsOpened: boolean;
  setInsightsOpened: (v: boolean | ((prev: boolean) => boolean)) => void;
  onGoalJourneyOpen?: (goalKey: string, goalLabel: string) => void;
  onGoalConversionLogOpen?: (goalKey: string, goalLabel: string, options?: { initialKind: 'undefined' }) => void;
  onSourceJourneyOpen?: (args: { sourceId: string; sourceName: string; goalKey: string; goalLabel: string }) => void;
  onTrackingJourneyOpen?: (args: { trackingTagData: string; entityLabel: string; goalKey: string; goalLabel: string }) => void;
  onCustomJourneyOpen?: (args: { tagId: string; tagValue: string; tagName: string; goalKey: string; goalLabel: string }) => void;
  onLifecycleVelocityOpen?: (args: {
    goalKey: string;
    goalLabel: string;
    cohortKey: string;
    cohortKind: 'daily' | 'range' | 'weekday' | 'weekend' | 'summary';
    cohortStart?: string;
    cohortEnd?: string;
  }) => void;
};

// Campaign-level columns never apply to a single goal row, so they are dropped; the rest follow
// Visible Metrics, so the columns read in the order the chooser lists them.
const GOAL_BREAKDOWN_HIDDEN = new Set(['spend', 'aov', 'profit', 'roi', 'uniques', 'visits', 'ctr', 'cpm']);
const GOAL_BREAKDOWN_ORDER = ALL_METRICS.filter((m) => !GOAL_BREAKDOWN_HIDDEN.has(m.toLowerCase()));

const EMPTY_PRIMARY_GOAL_SUMMARY: MetricSource = {
  spend: '$0.00',
  revenue: '$0.00',
  conversions: 0,
};

export function TotalTab({
  goalRows,
  goalCustomerType,
  primaryGoalSummary,
  lifecycleData,
  lifecycleLoading = false,
  isDarkTheme = false,
  insightsSubtitles,
  insightsChart,
  trendData,
  velocityData,
  insightsCompare,
  onInsightsCompareChange,
  cpcTrendMetric,
  onCpcTrendMetricChange,
  onVelocityFromChange,
  setInsightsChart,
  contributionData,
  sourcesData,
  openSettings,
  totalMetrics,
  primaryGoalNames = [],
  selectedPrimaryGoalName = '',
  advancedFilterTables,
  goalBreakdownOpened,
  goalBreakdownType,
  onGoalBreakdownTypeChange,
  setGoalBreakdownOpened,
  lifecycleOpened,
  setLifecycleOpened,
  insightsOpened,
  setInsightsOpened,
  onGoalJourneyOpen,
  onGoalConversionLogOpen,
  onSourceJourneyOpen,
  onTrackingJourneyOpen,
  onCustomJourneyOpen,
  onLifecycleVelocityOpen,
}: TotalTabProps) {

  const isContribution = insightsChart === 'contribution' || insightsChart === 'network_contribution';
  const isSources = insightsChart === 'sources';
  const trendKind = insightsChart === 'roas_trend' ? 'roas' : insightsChart === 'rev_vs_spend' ? 'rvs' : insightsChart === 'cpc_trends' ? 'cpc' : null;

  const hasDifferentPrimaryGoals = primaryGoalNames.length > 1;
  const hasSinglePrimaryGoal = primaryGoalNames.length === 1;
  const primaryGoalLabel = selectedPrimaryGoalName || (hasSinglePrimaryGoal ? primaryGoalNames[0] : '');
  const metricsBlocked = hasDifferentPrimaryGoals && !selectedPrimaryGoalName;
  const noCampaignContext = primaryGoalNames.length === 0;
  const summaryRow: MetricSource = primaryGoalSummary ?? EMPTY_PRIMARY_GOAL_SUMMARY;

  const goalBreakdownMetrics = GOAL_BREAKDOWN_ORDER.filter((m) =>
    totalMetrics.some((sel) => sel.toLowerCase() === m.toLowerCase())
      && !GOAL_BREAKDOWN_HIDDEN.has(m.toLowerCase())
  );

  return (
    <Tabs.Panel value="total" pt="lg">
      <Flex align="center" gap="xs" mb="sm" wrap="wrap">
        <SectionHelp section="summary" title="Summary metrics" />
        <Text size="sm" color="dimmed">
          Primary Goal:
        </Text>
        <Text size="sm" weight={700}>
          {primaryGoalLabel || ''}
        </Text>
      </Flex>

      <Paper
        withBorder
        radius="md"
        p="sm"
        sx={(theme) => ({
          backgroundColor:
            theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
        })}
      >
        <Flex justify="flex-end" mb={4}>
          <ActionIcon
            variant="default"
            size="md"
            title="Total Settings"
            onClick={() =>
              openSettings(
                'Total Settings',
                'Applies to Total · Goal Breakdown'
              )
            }
          >
            <IconSettings size="0.9rem" />
          </ActionIcon>
        </Flex>

        {noCampaignContext ? (
          <Text size="sm" color="dimmed" align="center" py="md">
            Select a Visiopt campaign in filters to load metrics.
          </Text>
        ) : totalMetrics.length === 0 ? (
          <Text size="sm" color="dimmed" align="center" py="md">
            No metrics selected. Open Total Settings to choose visible metrics.
          </Text>
        ) : metricsBlocked ? (
          <Text size="sm" color="dimmed" align="center" py="md">
            Choose a Primary Goal in filters to view summary metrics.
          </Text>
        ) : (
          <ScrollArea type="auto" offsetScrollbars>
            <Table
              verticalSpacing="md"
              horizontalSpacing="md"
              sx={{
                minWidth: totalMetrics.length * 110,
                '& th, & td': { whiteSpace: 'nowrap' },
              }}
            >
              <thead>
                <tr>
                  {totalMetrics.map((m) => (
                    <th key={m}>
                      <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                        {m}
                      </Text>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {totalMetrics.map((m) => (
                    <MetricCellTd
                      key={m}
                      row={summaryRow}
                      metric={m}
                      size="md"
                    />
                  ))}
                </tr>
              </tbody>
            </Table>
          </ScrollArea>
        )}
      </Paper>

      <Box mt="lg">
        <SectionHeader
          icon={<IconTarget size="1rem" color="#ef4444" />}
          label="Goal Breakdown"
          info={<SectionHelp section="goal" title="Goal Breakdown" breakdownType={goalBreakdownType} />}
          actionNode={<GoalBreakdownTypeSelect value={goalBreakdownType} onChange={onGoalBreakdownTypeChange} withSource />}
          opened={goalBreakdownOpened}
          toggle={() => setGoalBreakdownOpened((o) => !o)}
        />
        <Collapse in={goalBreakdownOpened}>
          <Paper
            withBorder
            radius="md"
            p="sm"
            mt="sm"
            sx={(theme) => ({
              backgroundColor:
                theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
            })}
          >
            <Text size="xs" color="dimmed" mb="sm">
              Click a conversion count to open its log, or the route icon to see customer journeys.
            </Text>
            <GoalBreakdownTable
              customerType={goalCustomerType}
              goalRows={goalRows}
              metrics={goalBreakdownMetrics}
              onJourneyOpen={
                onGoalJourneyOpen
                  ? (row) => onGoalJourneyOpen(row.goal_key ?? '', row.goal)
                  : undefined
              }
              onConversionLogOpen={
                onGoalConversionLogOpen
                  ? (row, kind) => onGoalConversionLogOpen(
                      row.goal_key ?? '',
                      row.goal,
                      kind === 'undefined' ? { initialKind: 'undefined' } : undefined
                    )
                  : undefined
              }
            />
          </Paper>
        </Collapse>
      </Box>

      {advancedFilterTables ? (
        <AdvancedFilterTables
          data={advancedFilterTables}
          metrics={totalMetrics}
          isDarkTheme={isDarkTheme}
          onSourceJourneyOpen={onSourceJourneyOpen}
          onTrackingJourneyOpen={onTrackingJourneyOpen}
          onCustomJourneyOpen={onCustomJourneyOpen}
        />
      ) : null}

      <Box mt="lg">
        <SectionHeader
          icon={<IconChartBar size="1rem" color="#3b82f6" />}
          label="Lifecycle"
          info={<SectionHelp section="lifecycle" title="Lifecycle" />}
          rightNode={
            <Badge color="blue" variant="outline" size="sm" sx={{ textTransform: 'none' }}>
              Cohort View
            </Badge>
          }
          opened={lifecycleOpened}
          toggle={() => setLifecycleOpened((o) => !o)}
        />
        <Collapse in={lifecycleOpened}>
          <AggregatedLifecycleBody
            lifecycleData={lifecycleData}
            loading={lifecycleLoading}
            isDarkTheme={isDarkTheme}
            onVelocityOpen={onLifecycleVelocityOpen}
          />
        </Collapse>
      </Box>

      <Box mt="lg">
        <SectionHeader
          icon={<IconReportAnalytics size="1rem" color="#22c55e" />}
          label="Insights"
          info={<SectionHelp section="insights" title="Insights" />}
          opened={insightsOpened}
          toggle={() => setInsightsOpened((o) => !o)}
        />
        <Collapse in={insightsOpened}>
          <Paper
            withBorder
            radius="md"
            p="md"
            mt="sm"
            sx={(theme) => ({
              backgroundColor:
                theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.gray[0],
            })}
          >
            <Chip.Group
              value={insightsChart}
              onChange={(v: any) => setInsightsChart(v as string)}
            >
              <Flex gap="xs" wrap="wrap" mb="md">
                <Chip value="contribution" color="teal" variant="outline">
                  Campaign Contribution
                </Chip>
                <Chip value="network_contribution" color="teal" variant="outline">
                  Ad Network Contribution
                </Chip>
                <Chip value="roas_trend" color="teal" variant="outline">
                  ROAS Trend
                </Chip>
                <Chip value="rev_vs_spend" color="teal" variant="outline">
                  Rev vs Spend
                </Chip>
                <Chip value="velocity" color="teal" variant="outline">
                  Velocity
                </Chip>
                <Chip value="cpc_trends" color="teal" variant="outline">
                  CPC Trends
                </Chip>
                <Chip value="sources" color="teal" variant="outline">
                  Sources
                </Chip>
              </Flex>
            </Chip.Group>

            {(isContribution || isSources) && (
              <Flex align="center" gap={8} mb="xs">
                <InsightsInfo chart={insightsChart} />
                <Text size="xs" color="dimmed">
                  {insightsSubtitles[insightsChart] || 'Select campaigns to load data'}
                </Text>
              </Flex>
            )}
            {/* Chart mounts only after open: collapsed container measures 0 width. */}
            {insightsOpened && (
              isContribution ? (
                <ContributionChart data={contributionData ?? null} isDarkTheme={isDarkTheme} />
              ) : isSources ? (
                <SourcesChart data={sourcesData ?? null} isDarkTheme={isDarkTheme} />
              ) : trendKind ? (
                <TrendChart
                  kind={trendKind}
                  data={trendData ?? null}
                  isDarkTheme={!!isDarkTheme}
                  compare={!!insightsCompare[insightsChart]}
                  onCompareChange={(v) => onInsightsCompareChange(insightsChart, v)}
                  costMetric={cpcTrendMetric}
                  onCostMetricChange={onCpcTrendMetricChange}
                />
              ) : (
                <VelocityChart
                  data={velocityData ?? null}
                  isDarkTheme={!!isDarkTheme}
                  onFromChange={onVelocityFromChange}
                />
              )
            )}
          </Paper>
        </Collapse>
      </Box>
    </Tabs.Panel>
  );
}
